/**
 * Every word either page can put on screen.
 *
 * Nothing in a view is written in English inline. The rule is the app's, and
 * it is not decoration: most of the people who will read these screens read
 * Amharic, and a string hardcoded in a view is a string that is only ever
 * going to appear in one language.
 *
 * Where the phone already says something, this says it the same way. Two
 * spellings of "verify" across two clients is one product that looks like two,
 * and `strings.test.ts` compares the shared keys against the phone's own
 * `en.json` rather than trusting this note.
 */

export type Lang = "en" | "am";

/** The remembered choice. A preference, so the browser is the right place. */
const LANG_KEY = "liyu.lang";

const en = {
  brandShort: "Liyu",

  "role.resident": "Resident",
  "role.accountant": "Accountant",
  "role.committee": "Committee",

  "common.signOut": "Sign out",
  "common.loading": "Loading",
  "common.lightMode": "Light mode",
  "common.darkMode": "Dark mode",
  "common.language": "አማርኛ",
  "common.nothing": "No records yet.",
  "common.problem": "Could not complete the request. Try again.",
  "refusal.signIn": "Sign in again to continue.",
  "refusal.notAllowed": "You do not have permission to do this.",
  "refusal.setup": "Finish setting up your account first.",
  "refusal.noBuilding": "Your account is not linked to a building. Contact Liyu support.",
  "refusal.suspended": "Your building's Liyu account is suspended. Contact your committee.",
  "refusal.notLive": "This building is not active yet.",
  "refusal.notInPlan":
    "Your building's plan does not include this feature. Contact your committee.",
  "refusal.invalid": "Check the form details and try again.",
  "app.suspended": "Your building's Liyu account is suspended. Contact your committee.",
  "app.notLiveYet": "This building is not active yet. Liyu is completing its setup.",

  "signIn.staff": "Staff",
  "signIn.privacyTitle": "Who can see your account",
  "signIn.privacy":
    "Only your building's committee and its accountant. No other building on Liyu can read it.",
  brandSuffix: "Management",
  "signIn.home": "Back to website",
  "signIn.eyebrow": "Building management",
  "signIn.title": "Sign in to Liyu",
  "signIn.lead":
    "Sign in with your building code and account details. Accountants and committee members should select Staff.",
  "signIn.orgCode": "Building code",
  "signIn.orgCodeHint": "On your sign-in slip",
  "signIn.unit": "Unit",
  "signIn.unitHint": "e.g. A-101",
  "signIn.staffId": "Staff ID",
  "signIn.staffIdHint": "e.g. committee",
  "signIn.password": "Password",
  "signIn.passwordHint": "Your password",
  "signIn.showPassword": "Show",
  "signIn.hidePassword": "Hide",
  "signIn.submit": "Sign in",
  "signIn.working": "Signing in",
  "signIn.checking": "Checking whether you are already signed in",
  "signIn.session": "Your session stays in this browser.",
  "signIn.wrongDoor": "Select the correct account type: Resident or Staff.",
  "signIn.refused": "Incorrect building code, ID or password.",
  "signIn.tooMany": "Too many attempts. Wait a minute and try again.",
  "signIn.offline": "No connection. Check the network and try again.",
  "reset.forgot": "Forgot your password?",
  "reset.title": "Reset your password",
  "reset.lead": "We will send a verification code to the phone number on your resident account.",
  "reset.staff": "Staff: contact Liyu support to reset your password.",
  "reset.phone": "Your phone number",
  "reset.send": "Send code",
  "reset.code": "Verification code",
  "reset.verify": "Verify code",
  "reset.back": "Back to sign in",
  "reset.badPhone": "Enter your phone number, like 0911 234 567.",
  "reset.badCode": "Invalid or expired code. Try again.",
  "reset.noAccount": "No account uses that phone number. Ask the office to reset your sign-in.",
  "reset.done": "Password changed.",
  "signIn.setupTitle": "Set up your account",
  "signIn.residentSetupLead":
    "Enter your name, verify your phone number and choose a new password.",
  "signIn.residentSetupVouched":
    "Your identity has been verified at the office. Enter your name, phone number and a new password.",
  "signIn.setupName": "Full name",
  "signIn.setupNameNeeded": "Enter your name, between 2 and 120 characters.",
  "signIn.setupPhone": "Phone number",
  "signIn.setupPhoneHint":
    "For account recovery and for building staff to reach you. Write it like 0911 234 567.",
  "signIn.setupPhoneTaken": "That number is already confirmed on another account. Ask the office.",
  "signIn.setupLead": "Choose a new password to replace your temporary password.",
  "signIn.newPassword": "New password",
  "signIn.newPasswordAgain": "Confirm password",
  "signIn.setupSubmit": "Save password",
  "signIn.setupWorking": "Saving",
  "signIn.setupMismatch": "Passwords do not match.",
  "signIn.setupTooShort": "Use at least 8 characters.",
  "signIn.setupNeedsLetterAndNumber": "Use at least one letter and one number.",

  "home.rights": "Liyu Management",
  "home.tagline": "Building management",

  "statement.export": "Export statement",
  "statement.exporting": "Preparing statement",
  "statement.done": "Statement downloaded.",
  "statement.hint":
    "Download a spreadsheet of this month's charges, payments, expenses and transactions.",
  "statement.title": "Monthly statement",
  "statement.building": "Building",
  "statement.month": "Month",
  "statement.preparedBy": "Prepared by",
  "statement.preparedOn": "Prepared on",
  "statement.summary": "Monthly summary",
  "statement.charged": "Charged",
  "statement.collected": "Collected",
  "statement.stillOwed": "Outstanding",
  "statement.spent": "Expenses",
  "statement.net": "Net income",
  "statement.held": "Held funds",
  "statement.costsByKind": "Expenses by category",
  "statement.stillOwing": "Units with unpaid fees",
  "statement.everyEntry": "Monthly transactions",
  "statement.kind": "Type",
  "statement.charge": "Charge",
  "statement.payment": "Payment",
  "statement.where": "Payment method",
  "statement.who": "Recorded by",
  "statement.note": "Note",
  "statement.corrects": "Corrects entry",

  "nav.home": "Home",
  "nav.pay": "Pay fees",
  "nav.repairs": "Repairs",
  "nav.notices": "Notices",
  "nav.give": "Contribute",
  "nav.advertisements": "Contractors",
  "fix.title": "Corrections",
  "fix.record": "Record a payment",
  "fix.amount": "Amount",
  "fix.why": "Reason",
  "fix.whyHint": "Visible to the committee.",
  "fix.adjust": "Adjust balance",
  "fix.add": "Increase balance",
  "fix.reduce": "Reduce balance",
  "fix.whichMonth": "Month",
  "fix.voidMonth": "Cancel a month's bill",
  "fix.voidHint": "This reverses the charge rather than deleting it.",
  "fix.changeBilling": "Edit monthly fee",
  "fix.monthlyDue": "Monthly fee",
  "fix.paused": "Paused",
  "fix.nextRunOnly": "Applies from next month. Existing bills will not change.",
  "fix.save": "Save",
  "fix.takeBack": "Reverse payment",
  "fix.reverseHint": "Enter a reason, e.g. failed transfer or duplicate payment.",
  "fix.post": "Save adjustment",
  "fix.cancelBill": "Cancel this bill",
  "held.heading": "Held funds",
  "held.none": "No held funds to review.",
  "held.settle": "Resolve held funds",
  "held.whyHint": "Visible to the resident and committee.",
  "bk.void": "Cancel expense",
  "bk.voided": "Cancelled.",
  "bk.accounts": "Accounts",
  "bk.recount": "Update balance",
  "bk.balance": "Current balance",
  "bk.neverCounted": "No balance recorded.",
  "bk.noAccounts": "No accounts yet. Add one below.",
  "bk.building": "Billing settings",
  "bk.defaultDue": "Default monthly fee",
  "bk.dueDay": "Payment due day",
  "bk.buildingHint": "Applies from next month. Existing bills will not change.",
  "bk.changeBuilding": "Edit billing settings",
  "bk.addAccount": "Add an account",
  "bk.accountName": "Account name",
  "held.refunded": "Record refund",
  "held.waived": "Keep as building funds",
  "held.extended": "Apply to unpaid fees",
  "fix.done": "Done.",
  "signInReset.title": "Reset resident password",
  "signInReset.hint":
    "Issue a temporary password when a resident cannot reset their password by phone. They must choose a new password at next sign-in.",
  "signInReset.button": "Issue temporary password",
  "signInReset.issued":
    "New one-time password for {{unit}}. Give it to the resident in person; it is not shown again.",
  "bk.countedOn": "Updated {{date}}",
  "fix.needAmount": "Enter an amount in whole birr.",
  "ads.add": "Add contractor",
  "ads.edit": "Edit contractor",
  "ads.remove": "Remove contractor",
  "ads.save": "Save contractor",
  "ads.businessName": "Name (person or business)",
  "trade.plumbing": "Plumbing",
  "trade.electrical": "Electrical",
  "trade.lift": "Lift repair",
  "trade.carpentry": "Carpentry",
  "trade.painting": "Painting",
  "trade.masonry": "Building and tiling",
  "trade.metalwork": "Gates and welding",
  "trade.cleaning": "Cleaning",
  "trade.security": "Security",
  "trade.pestControl": "Pest control",
  "trade.gardening": "Gardening",
  "trade.other": "Other",
  "ads.trade": "Trade",
  "ads.chooseTrade": "Choose a trade",
  "ads.needTrade": "Choose the contractor's trade.",
  "contractors.allTrades": "All trades",
  "contractors.search": "Search contractors",
  "contractors.searchHint": "Name",
  "contractors.showing": "{{shown}} of {{total}} contractors",
  "contractors.none": "No contractors match these filters.",
  "contractors.noneOfTrade": "No contractors listed for this trade.",
  "repairs.findWorker": "Find contractor",
  "repairs.hideWorkers": "Hide contractors",
  "ads.description": "Description",
  "ads.phone": "Phone number",
  "ads.explain":
    "Contractors listed by your committee. Contact a business directly to arrange work.",
  "repairs.move.assigned": "Mark in progress",
  "repairs.move.attended": "Mark as completed",
  "repairs.move.closed": "Close request",
  "repairs.move.reported": "Reopen",
  "repairs.assignedTo": "{{name}} ({{trade}}) · {{phone}}",
  "ads.saved": "Saved.",
  "ads.removed": "Removed.",
  "ads.bad": "Complete all fields. Enter the phone number like 0911 234 567.",
  "repairs.moved": "Updated.",
  "repairs.call": "Call",
  "repairs.whatsapp": "WhatsApp",
  "repairs.telegram": "Telegram",
  "repairs.message": "Repair at {{org}}, unit {{unit}}: {{what}}. Please call the committee.",
  "resident.notFixed": "Still not fixed",
  "resident.sentBack": "Sent back to the committee.",
  "advertisements.none": "The committee has not listed any contractors yet.",
  "nav.queue": "Verify payments",
  "nav.units": "Units",
  "nav.books": "Finances",
  "nav.arrears": "Unpaid fees",
  "nav.journal": "Activity log",
  "nav.overview": "Overview",
  "nav.campaigns": "Fundraising",

  "resident.owe": "Outstanding balance",
  "resident.oweNone": "No outstanding fees",
  "resident.oweMonths_one": "{{count}} month outstanding",
  "resident.oweMonths_other": "{{count}} months outstanding",
  "resident.waiting": "Pending verification",
  "resident.waitingCount_one": "{{count}} pending payment",
  "resident.waitingCount_other": "{{count}} pending payments",
  "resident.openRepairs": "Open repairs",
  "resident.openRepairsSub": "Affecting you or shared areas",
  "resident.months": "Monthly fees",
  "resident.latestNotice": "Latest notice",
  "resident.paymentReminder": "Payment reminder",
  "resident.paymentReminderBody":
    "{{amount}} is unpaid across {{months}} month(s). Select Pay fees to submit your payment.",
  "resident.dismissReminder": "Dismiss",
  "resident.allNotices": "All notices",
  "resident.balance": "Outstanding balance",
  "resident.paymentAmount": "Amount to pay (ETB)",
  "queue.customAmount": "Custom amount",
  "resident.reference": "Transaction number",
  "resident.referenceHint": "The transaction number on your receipt",
  "resident.submit": "Submit payment",
  "telebirr.notYet": "Coming soon. Use a bank transfer for now.",
  "telebirr.or": "Or pay with telebirr",
  "telebirr.hint":
    "Pay from your Telebirr wallet. Your balance updates after Telebirr confirms the payment. No receipt is needed.",
  "telebirr.pay": "Pay with telebirr",
  "telebirr.title": "Your telebirr payment",
  "telebirr.waiting":
    "Waiting for Telebirr to confirm your payment. This page updates by itself once it does.",
  "telebirr.check": "Check payment status",
  "telebirr.paid": "Telebirr confirmed your {{amount}} payment. Your account has been updated.",
  "telebirr.needsReview":
    "Payment received by Telebirr and awaiting accountant review. Do not pay again.",
  "telebirr.failed": "Telebirr could not start the payment. Nothing was taken.",
  "chapa.or": "Or pay online: telebirr, CBE Birr, bank or card",
  "chapa.hint":
    "Pays the amount above through Chapa, with telebirr, CBE Birr, your bank or a card. It counts once Chapa confirms it to Liyu, with no receipt needed.",
  "chapa.pay": "Pay with Chapa",
  "chapa.title": "Your Chapa payment",
  "chapa.waiting":
    "Waiting for Chapa to confirm your payment. This page updates by itself once it does.",
  "chapa.check": "Check with Chapa again",
  "chapa.paid": "Chapa confirmed {{amount}}. It is on your account.",
  "chapa.needsReview":
    "Chapa took the payment, and the accountant is checking it. You don't need to pay again.",
  "chapa.failed": "Chapa could not start the payment. Nothing was taken.",
  "nav.chapa": "Chapa",
  "chapa.receipts": "Chapa receipts",
  "chapa.receiptsLead":
    "Chapa confirmed these payments itself, so they need no checking and the units are already marked paid. A payment Chapa took that Liyu could not match waits in Verify payments.",
  "chapa.money": "Money paid through Chapa",
  "chapa.moneyLead":
    "Chapa sends each payment to the committee's bank account about 24 hours after the resident pays. These figures are the payments Chapa confirmed; the bank statement is the final word.",
  "chapa.onTheWay": "On its way to the bank",
  "chapa.reached": "In the committee's bank",
  "chapa.total": "Paid through Chapa",
  "chapa.count_one": "{{count}} payment",
  "chapa.count_other": "{{count}} payments",
  "chapa.lastArrival": "All of it in the bank by about {{when}}",
  "chapa.arrivesBy": "On its way, in the bank by about {{when}}",
  "chapa.inBank": "In the bank",
  "chapa.paidAt": "Paid",
  "chapa.where": "Where the money is",
  "chapa.status": "State",
  "chapa.verified": "Confirmed by Chapa",
  "chapa.submitted": "Waiting in Verify payments",
  "chapa.rejected": "Turned down",
  "chapa.none": "Nobody has paid with Chapa yet.",
  "chapa.receiptOpen": "Open receipt",
  "chapa.receiptTitle": "Chapa receipt",
  "chapa.receiptLoading": "Asking Chapa for this receipt…",
  "chapa.receiptClose": "Close receipt",
  "chapa.receiptMethod": "Paid with",
  "chapa.receiptPayer": "Paid by",
  "chapa.receiptCovers": "What it paid",
  "chapa.receiptHeld": "{{amount}} held for you to decide",
  "chapa.receiptChapaRef": "Chapa reference",
  "chapa.receiptLiyuRef": "Liyu reference",
  "chapa.receiptChapaSays": "Chapa says",
  "chapa.receiptConfirmed": "Paid, and the amount matches",
  "chapa.receiptMismatch":
    "Chapa reports {{status}}, {{amount}}. Check this payment in the Chapa dashboard before relying on it.",
  "chapa.receiptChecked": "Checked with Chapa at {{when}}",
  "chapaReturn.title": "Back to Liyu",
  "chapaReturn.lead":
    "Opening the Liyu app, where your payment is checked with Chapa. If it does not open by itself, tap the button.",
  "chapaReturn.open": "Open the Liyu app",
  "chapaReturn.noOrder":
    "This link is missing its payment. Open the Liyu app and go to Pay fees to see your payment.",
  "footer.runBy": "Liyu is run by NADAWI Technologies",
  "home.receiptNo": "No.",
  "home.currency": "ETB",
  "home.unpaid": "{{months}} unpaid",
  "home.withAccountant": "{{amount}} is with the accountant to check",
  "home.pay": "Pay {{amount}}",
  "home.payFees": "Pay fees",
  "home.methods": "telebirr · CBE Birr · bank · card",
  "home.year": "{{year}} E.C.",
  "home.yearSub": "Your year, month by month",
  "home.notDue": "not due yet",
  "home.now": "now",
  "home.earlier": "Earlier months still owing",
  "home.repairs": "Repairs · {{count}} open",
  "home.noNotices": "The committee hasn't posted anything yet.",
  "home.notice": "Latest notice",
  "home.history": "Past payments",
  "home.historySub": "What you sent, and what the accountant decided",
  "pay.receipt": "Receipt",
  "pay.from": "From",
  "pay.amount": "Amount",
  "pay.for": "For",
  "pay.to": "Paid to",
  "pay.unit": "Unit {{unit}}",
  "pay.oldest": "{{month}} only · {{amount}}",
  "pay.all": "All of it · {{amount}}",
  "pay.nothingOwed": "Nothing is owed now; a payment is held for the accountant.",
  "pay.held": "and {{amount}} held for the accountant",
  "pay.withChapa": "Pay {{amount}} with Chapa",
  "pay.aheadOne": "And next month · {{amount}}",
  "pay.aheadMany": "And {{count}} months ahead · {{amount}}",
  "pay.anyAmount":
    "Or type any amount in whole birr. What is more than you owe is held for the accountant.",
  "paid.title": "Payment verified",
  "paid.body":
    "Chapa confirmed {{amount}}. It is on your account, so there is nothing more to pay for it.",
  "paid.leaving": "Taking you to Home…",
  "paid.home": "Go to Home",
  "history.title": "Payment history",
  "history.lead": "Newest first. Every payment you sent and what became of it.",
  "history.open": "View payment history",
  "history.none": "You haven't sent a payment yet.",
  "history.covered": "Covered {{months}}.",
  "history.waiting":
    "Pending verification. Your balance will update once the accountant verifies your payment.",
  "history.viaChapa": "Paid through Chapa",
  "history.viaBank": "Bank transfer",
  "history.towards": "Towards {{title}}",
  "history.whyTurnedDown": "Rejection reason",
  "history.sendAgain": "Resubmit payment",
  "history.extra.unresolved":
    "{{amount}} of this was more than you owed. It is with the accountant, who will refund it, apply it to your fees or keep it for the building.",
  "history.extra.refunded":
    "{{amount}} of this was more than you owed. The accountant recorded it as refunded to you.",
  "history.extra.waived":
    "{{amount}} of this was more than you owed. The accountant kept it as building funds.",
  "history.extra.extended":
    "{{amount}} of this was more than you owed. The accountant applied it to your {{month}} fees.",
  "pay.bankTitle": "Already paid by bank transfer?",
  "pay.bankLead":
    "Send the reference and a photo of the receipt. The accountant checks it before it counts.",
  "resident.partialPaymentHint":
    "Enter an amount in whole birr. Once verified, it reduces your oldest unpaid fees. Any excess is held for review.",
  "resident.invalidPaymentAmount": "Enter a whole-birr amount of at least ETB 1.",
  "resident.sent": "Payment submitted. Your balance will update after verification.",
  "resident.receipt": "Receipt photo",
  "resident.receiptHint": "A photo or screenshot of the transfer, under 10 MB.",
  "resident.needReceipt": "Add a photo of the transfer receipt.",
  "resident.needReference": "Enter the transfer's reference, as it appears on the receipt.",
  "resident.confirmFixed": "Confirm repair",
  "resident.confirmedFixed": "Repair confirmed. The committee can now close the request.",
  "resident.newRepair": "Report a repair",
  "resident.whatBroke": "Issue",
  "resident.whatBrokeHint": "e.g. kitchen leak",
  "resident.raise": "Submit request",
  "resident.yourRequests": "Your requests",
  "resident.needTitle": "Enter a description of the issue.",
  "resident.raised": "Sent to the committee.",
  "resident.giveOpen": "Active campaign",
  "resident.giveAmount": "Amount",
  "resident.giveSend": "Submit contribution",
  "resident.giveHint": "Contributions count toward the total after verification.",
  "resident.giveAmountBad": "Enter an amount in whole birr.",
  "resident.gaveIt": "Contribution submitted for verification.",
  "resident.raisedOf": "{{raised}} of {{target}}",

  "queue.waiting": "Pending verification",
  "queue.waitingSub": "{{amount}} total",
  "queue.held": "Held funds",
  "queue.heldSub": "Awaiting a decision",
  "queue.collected": "Verified payments",
  "queue.collectedSub": "Verified and recorded",
  "queue.heading": "Pending payments",
  "queue.clear": "No pending payments",
  "queue.clearSub": "All submitted payments have been reviewed.",
  "queue.unit": "Unit",
  "queue.review": "Review details",
  "queue.hideReview": "Hide details",
  "queue.paymentId": "Payment ID",
  "queue.block": "Block",
  "queue.campaign": "Campaign",
  "queue.noReceipt": "No receipt was attached",
  "queue.months": "Monthly fees",
  "queue.amount": "Amount",
  "queue.reference": "Reference",
  "queue.when": "Sent",
  "queue.verify": "Verify payment",
  "queue.reject": "Reject payment",
  "queue.verified": "Verified and credited.",
  "queue.rejected": "Payment rejected. The resident can resubmit it.",
  "queue.why": "Rejection reason",
  "queue.needWhy": "Enter a reason so the resident can correct the submission.",

  "units.heading": "Units",
  "units.sub": "Accounts by block",
  "units.all": "All",
  "units.block": "Block {{block}}",
  "units.blockColumn": "Block",
  "units.current": "Up to date",
  "units.status": "Status",
  "units.balance": "Owed",
  "units.allBlocks": "All blocks",
  "units.filterStatus": "Payment",
  "units.search": "Find a unit",
  "units.searchHint": "e.g. GA01",
  "units.sort": "Order",
  "units.sortUnit": "By unit number",
  "units.sortOwed": "Most owed first",
  "units.showing": "{{shown}} of {{total}} units · {{owed}} owed",
  "units.clear": "Clear filters",
  "units.noMatch": "No units match these filters.",
  "units.details": "Unit details",
  "units.closeDetails": "Close details",
  "units.paymentHistory": "Payment history",
  "units.noPayments": "No payments recorded for this unit yet.",
  "units.paymentStatus": "Payment status",
  "units.paymentReference": "Reference",
  "units.paymentDate": "Submitted",
  "units.receipt": "View receipt",
  "units.receiptLoading": "Opening receipt…",
  "units.receiptUnavailable": "Receipt unavailable",
  "units.zoomOut": "−",
  "units.zoomReset": "100%",
  "units.zoomIn": "+",
  "units.fullscreen": "Full screen",
  "units.closeReceipt": "Close",

  "books.collected": "Verified payments",
  "books.collectedSub": "Verified and recorded",
  "books.spent": "Expenses",
  "books.spentSub": "Recorded expenses",
  "books.net": "Net income",
  "books.netSub": "Verified fee payments minus expenses",
  "books.outstanding": "Outstanding fees",
  "books.outstandingSub": "Across every unit",
  "books.record": "Record expense",
  "books.amount": "Amount",
  "books.category": "Category",
  "books.description": "Description",
  "books.descriptionHint": "Visible to the committee.",
  "books.vendor": "Paid to",
  "books.vendorHint": "Business or recipient name",
  "books.add": "Save expense",
  "books.recent": "Recent expenses",
  "books.date": "Date",
  "books.needAmount": "Enter an amount in whole birr.",
  "books.needDescription": "Enter an expense description.",
  "books.needVendor": "Enter the recipient name.",
  "books.recorded": "Expense recorded.",

  "arrears.behind": "Units with unpaid fees",
  "arrears.total": "Total outstanding",
  "arrears.inQueue": "Pending verification",
  "arrears.inQueueSub": "Payments awaiting verification",
  "arrears.worstFirst": "Most owed first",
  "arrears.remind": "Send a reminder",
  "arrears.reminded": "Reminder sent.",
  "arrears.remindTitle": "Payment reminders",
  "arrears.remindExplain":
    "Residents receive the reminder as a notification on their phone and in their Liyu inbox, showing the amount they owe.",
  "arrears.note": "Message (optional)",
  "arrears.noteHint": "For example: The office is open Saturday morning for cash payments.",
  "arrears.remindAll": "Send reminder to {{count}} units",
  "arrears.confirmAll": "Send a payment reminder to {{count}} units?",
  "arrears.remindedAll": "Reminders sent to {{count}} units.",
  "arrears.oweLine": "{{amount}} outstanding for {{count}} months.",
  "inbox.title": "Notifications",
  "inbox.unread": "{{count}} unread",
  "inbox.none":
    "No notifications yet. Payment reminders and updates from your building appear here.",
  "inbox.new": "New",
  "inbox.back": "All notifications",
  "inbox.missing": "This notification is no longer available.",
  "arrears.none": "No overdue fees.",

  "overview.collected": "Verified payments",
  "analytics.outstanding": "Outstanding this month",
  "analytics.open": "View breakdown",
  "analytics.choose": "Financial categories",
  "analytics.allMonths": "Across all months",
  "analytics.collectedHint": "Verified fee payments, month by month.",
  "analytics.outstandingHint": "Fees billed but not yet paid, month by month.",
  "analytics.netHint": "This month's verified fees minus this month's expenses.",
  "analytics.spentHint": "This month's expenses by category. Cancelled expenses are excluded.",
  "analytics.arrearsHint": "Each unit's total debt, grouped by its oldest unpaid month.",
  "analytics.overMonths": "Monthly trend",
  "analytics.whatOn": "Spending by category",
  "analytics.inAndOut": "Income and expenses this month",
  "analytics.howLate": "Age of unpaid fees",
  "analytics.current": "This month",
  "analytics.one": "1 month overdue",
  "analytics.two": "2 months overdue",
  "analytics.threePlus": "3 months or more",
  "analytics.empty": "No amounts to show yet.",
  "analytics.billedVerified": "Billed and verified, month by month",
  "analytics.billedOutstanding": "Billed and outstanding, month by month",
  "analytics.verified": "Paid and verified",
  "analytics.outstandingNote":
    "Outstanding includes payments still waiting for the accountant to verify.",
  "analytics.unpaid": "Outstanding",
  "analytics.soFar": "so far",
  "analytics.columnLine": "{{month}}: {{billed}} billed, {{verified}} verified, {{rate}} collected",
  "analytics.byBlock": "By block, least collected first",
  "analytics.blockLine": "{{percent}} collected",
  "analytics.whereWent": "Where this month's fees went",
  "analytics.stepIn": "Verified fees",
  "analytics.stepNet": "Left over",
  "analytics.stepShort": "Short",
  "analytics.netMonths": "Net income, month by month",
  "analytics.loss": "Month at a loss",
  "analytics.more": "{{amount}} more than last month",
  "analytics.less": "{{amount}} less than last month",
  "analytics.same": "Same as last month",
  "analytics.new": "None last month",
  "analytics.owingMost": "Units owing the most",
  "analytics.monthsOwed_one": "{{count}} month owed",
  "analytics.monthsOwed_other": "{{count}} months owed",
  "analytics.unitsCount_one": "{{count}} unit",
  "analytics.unitsCount_other": "{{count}} units",
  "analytics.figures": "Show the figures",
  "analytics.month": "Month",
  "analytics.billed": "Billed",
  "analytics.rate": "Collected",
  "analytics.spentCol": "Expenses",
  "analytics.netCol": "Net",
  "analytics.average": "Average verified per finished month: {{amount}}",
  "analytics.surplus": "Money left over",
  "overview.spent": "Expenses",
  "overview.net": "Net income",
  "overview.arrears": "Total outstanding",
  "overview.arrearsSub": "{{unpaid}} unpaid, {{pending}} pending verification",
  "overview.rate": "Collection rate",
  "overview.rateRow": "Billed fees collected",
  "overview.attention": "Needs attention",
  "overview.queueRow": "Pending payments",
  "overview.queueSub": "Awaiting verification",
  "overview.repairsRow": "Open repairs",
  "overview.repairsSub": "Maintenance requests",
  "overview.heldRow": "Held funds",
  "overview.heldSub": "Awaiting a decision",
  "overview.byBlock": "By block",
  "overview.blockLine": "{{rate}}% collected, {{owed}} outstanding",

  "campaigns.start": "Create campaign",
  "campaigns.what": "Campaign name",
  "campaigns.whatHint": "e.g. water tank",
  "campaigns.target": "Target",
  "campaigns.begin": "Create campaign",
  "campaigns.all": "All campaigns",
  "campaigns.close": "Close campaign",
  "campaigns.needBoth": "Enter a campaign name and target amount.",
  "campaigns.started": "Campaign created.",
  "campaigns.wasClosed": "Campaign closed.",

  "bk.autoReminders": "Automatic payment reminders",
  "bk.autoRemindersHint":
    "Once a month, {{days}} days after the due date, every unit still owing gets a reminder on their phone. Next: {{next}}.",
  "occupancy.title": "Resident account",
  "occupancy.noAccount": "No resident account for this unit. Create one in the admin console.",
  "occupancy.setUp": "set up",
  "occupancy.waiting": "waiting for first login",
  "occupancy.switchedOff": "inactive",
  "occupancy.switchOff": "Deactivate account",
  "occupancy.switchOn": "Activate account",
  "occupancy.switchOffWhy":
    "Enter a reason for deactivating this account. The resident will lose access. Existing records will be kept.",
  "occupancy.switchOnWhy": "Enter a reason for activating this account.",
  "occupancy.handOver": "Change resident",
  "occupancy.handOverHint":
    "For when the unit changes hands. The new occupant gets a one-time password and cannot see the previous occupant's payments or receipts. What the unit owes stays with the unit.",
  "occupancy.handOverWhyHint": "New tenant from 1 Tikimt, lease on file",
  "occupancy.handOverButton": "Change resident",
  "occupancy.handOverConfirm":
    "Assign {{unit}} to a new resident? The current resident will lose access.",
  "occupancy.handedOver":
    "One-time password for the new occupant of {{unit}}. Give it to them in person; it is not shown again.",
  "occupancy.carried": "The previous occupancy left {{amount}} owing, which stays on the unit.",
  "journal.automatic": "Automatic",
  "notices.publish": "Post a notice",
  "notices.noticeTitle": "Title",
  "notices.message": "Message",
  "notices.post": "Post notice",
  "notices.posted": "Posted.",
  "notices.published": "Posted notices",
  "notices.needBoth": "A title and a message are both needed.",
  "notices.keepUp": "Display duration",
  "notices.untilRemoved": "Until it is removed",
  "notices.forDays_one": "For {{count}} day",
  "notices.forDays_other": "For {{count}} days",
  "notices.urgent": "Urgent",
  "notices.urgentHint": "Urgent notices go to the top and are also sent to every resident's phone.",
  "notices.urgentMark": "Urgent",
  "notices.until": "Until {{date}}",
  "notices.remove": "Remove",
  "notices.removeConfirm": "Remove this notice for everyone?",
  "nav.chat": "Chat",
  "chat.actionFailed": "Couldn't save that. Check your connection and try again.",
  "chat.clearName": "Reset name to {{unit}}",
  "chat.committee": "Committee",
  "chat.committeeOnly": "Committee only",
  "chat.cooldown": "Wait between messages",
  "chat.cooldownHint":
    "Residents wait this long after each message they send. The committee never waits.",
  "chat.cooldownNone": "No wait",
  "chat.deleteConfirm": "Delete this message for everyone? It can't be brought back.",
  "chat.deleteMessage": "Delete message",
  "chat.done": "Close settings",
  "chat.everyone": "Everyone",
  "chat.forADay": "For 1 day",
  "chat.forAnHour": "For 1 hour",
  "chat.forAWeek": "For 1 week",
  "chat.isOff": "Chat is off until the committee turns it back on.",
  "chat.isOffUntil": "Chat is off until {{time}}.",
  "chat.isOn": "Chat is on.",
  "chat.locked": "Only the committee can post right now. You can still read the chat.",
  "chat.lockedCommittee": "Only the committee can post right now.",
  "chat.manage": "Chat settings",
  "chat.manageName": "Manage {{name}}",
  "chat.messageActions": "Message",
  "chat.messageActionsHint": "Opens pin, delete and the sender's chat",
  "chat.minutes": "{{count}} min",
  "chat.nameHintCommittee": "Leave it blank to show as Committee.",
  "chat.nameHintResident":
    "Leave it blank to show as {{unit}}. Your unit is always shown beside your name.",
  "chat.nameLabel": "Name in the chat",
  "chat.nobodyYet": "No residents have opened the chat yet.",
  "chat.noFundraisers": "No fundraisers are open.",
  "chat.none": "No messages yet. Say hello to the building.",
  "chat.off": "The committee has turned off your chat. You can still read it.",
  "chat.offUntil": "The committee has turned off your chat until {{time}}. You can still read it.",
  "chat.people": "People in the chat",
  "chat.peopleHint":
    "Everyone who has opened the chat. Nobody can be removed, because they still live here, but their chat can be turned off.",
  "chat.pinFundraisers": "Fundraisers pinned to the chat",
  "chat.pinFundraisersHint": "Up to {{count}}. Residents tap one to contribute.",
  "chat.pinMessage": "Pin message",
  "chat.pinned": "Pinned",
  "chat.placeholder": "Message the building",
  "chat.problem": "Couldn't load the chat. Check your connection.",
  "chat.rename": "Change your name",
  "chat.saveName": "Save name",
  "chat.seconds": "{{count}} sec",
  "chat.send": "Send",
  "chat.sendFailed": "Couldn't send. Wait a moment, check your connection, and try again.",
  "chat.turnOff": "Turn off chat",
  "chat.turnOffHint": "They can still read the chat. They can't send messages until it is back on.",
  "chat.turnOn": "Turn chat back on",
  "chat.unpinMessage": "Unpin message",
  "chat.untilTurnedOn": "Until turned back on",
  "chat.wait": "You can send your next message in {{count}} sec.",
  "chat.whoPosts": "Who can post",
  "chat.whoPostsHint":
    "Committee only is for when the chat needs to be quiet, such as during an emergency. Residents can still read.",
  "chat.you": "You",
  "chat.yourName": "Your name: {{name}}",
  "chat.close": "Close",
  "chat.cancel": "Cancel",
  "chat.unread": "{{count}} new",
  "chat.sendHint": "Enter sends. Shift and Enter starts a new line.",

  "repairs.reported": "Reported",
  "repairs.assigned": "Assigned",
  "repairs.attended": "Awaiting resident confirmation",
  "repairs.confirmed": "Confirmed fixed",
  "repairs.closed": "Closed",

  /* The change log's own names, and the kinds a repair can be, taken
     verbatim from the phone so one action is not called two things. */
  "action.account.active": "Account status changed",
  "action.account.reconciled": "Account balance updated",
  "action.account.reset": "Password reset",
  "action.account.setUp": "Account set up",
  "action.account.slipsIssued": "Sign-in slips issued",
  "action.units.added": "Units added",
  "action.account.vouched": "Resident identity verified in person",
  "action.building.changed": "Building settings changed",
  "action.campaign.closed": "Campaign closed",
  "action.campaign.created": "Campaign started",
  "action.contribution.reversed": "Contribution reversed",
  "action.contribution.verified": "Contribution verified",
  "action.expense.recorded": "Expense recorded",
  "action.expense.voided": "Expense voided",
  "action.heldFunds.resolved": "Held funds resolved",
  "action.invoice.voided": "Invoice voided",
  "action.invoices.raised": "Invoices raised",
  "action.ledger.adjusted": "Ledger adjusted",
  "action.organization.created": "Building added",
  "action.organization.status": "Building status changed",
  "action.subscription.set": "Liyu plan changed",
  "action.subscription.planChanged": "Plan terms changed",
  "action.telebirr.configured": "Telebirr account changed",
  "action.chapa.configured": "Chapa subaccount changed",
  "action.payment.recorded": "Payment recorded manually",
  "action.payment.rejected": "Payment rejected",
  "action.payment.reversed": "Payment reversed",
  "action.payment.verified": "Payment verified",
  "action.reminders.sent": "Reminders sent",
  "action.staff.created": "Staff account created",
  "action.unit.changed": "Unit changed",
  "action.unit.handover": "Unit handed over",
  "category.cleaning": "Cleaning",
  "category.electrical": "Electricity",
  "category.government": "Government",
  "category.lift": "Lift",
  "category.maintenance": "Maintenance",
  "category.other": "Other",
  "category.plumbing": "Plumbing",
  "category.salaries": "Wages",
  "category.security": "Security",
  "category.utilities": "Water and power bills",

  "state.verified": "Paid",
  "state.unverified": "Pending verification",
  "state.unpaid": "Unpaid",
  "state.partial": "Partially paid",
  "state.inactive": "Not billed",
} as const;

export type Key = keyof typeof en;

/**
 * The stem of a plural pair, which is what a call site actually asks for.
 *
 * `t("resident.oweMonths", { count })` picks between `_one` and `_other`; the
 * stem itself is never a key in the table, so it has to be nameable in the
 * type without being one.
 */
type StemOf<K> = K extends `${infer Stem}_one` ? Stem : never;

/**
 * Through a generic, deliberately. A conditional type only distributes over a
 * naked type parameter, so writing it against `Key` directly tests the whole
 * union at once and collapses to `never`.
 */
export type PluralKey = StemOf<Key>;

/**
 * The same words in Amharic.
 *
 * Where the phone already carries a translation, that exact wording is reused.
 * Every key the English has, this has: `strings.test.ts` fails otherwise, and
 * a missing one would fall back to English silently, which reads perfectly and
 * in the wrong language.
 */
const am: Record<Key, string> = {
  brandShort: "ልዩ",

  "role.resident": "ነዋሪ",
  "role.accountant": "ሒሳብ ሠራተኛ",
  "role.committee": "ኮሚቴ",

  "common.signOut": "ውጣ",
  "common.loading": "በመጫን ላይ",
  "common.lightMode": "ብርሃን ሁነታ",
  "common.darkMode": "ጨለማ ሁነታ",
  "common.language": "English",
  "common.nothing": "እስካሁን ምንም የለም",
  "common.problem": "አልተሳካም። እንደገና ይሞክሩ።",
  "refusal.signIn": "ለመቀጠል እንደገና ይግቡ።",
  "refusal.notAllowed": "መለያዎ ይህን እዚህ ማድረግ አይችልም።",
  "refusal.setup": "መጀመሪያ የመለያዎን ዝግጅት ይጨርሱ።",
  "refusal.noBuilding": "መለያዎ ከሕንፃ ጋር አልተያያዘም። ሊዩ እንዲያስተካክለው ይጠይቁ።",
  "refusal.suspended": "የዚህ ሕንፃ የሊዩ መለያ ታግዷል። ኮሚቴዎ ሊዩን እንዲያነጋግር ይጠይቁ።",
  "refusal.notLive": "ይህ ሕንፃ ገና አልተከፈተም።",
  "refusal.notInPlan": "የዚህ ሕንፃ የሊዩ ዕቅድ ይህን አያካትትም። ኮሚቴዎ ሊዩን እንዲያነጋግር ይጠይቁ።",
  "refusal.invalid": "ቅጹ ላይ ያልተስተካከለ ነገር አለ። ይፈትሹና እንደገና ይሞክሩ።",
  "app.suspended": "የዚህ ሕንፃ የሊዩ መለያ ታግዷል። ኮሚቴዎ ሊዩን እንዲያነጋግር ይጠይቁ።",
  "app.notLiveYet": "ይህ ሕንፃ ገና አልተከፈተም። ሊዩ ዝግጅቱን ሲጨርስ ይከፈታል።",

  "signIn.staff": "ሠራተኛ",
  "signIn.privacyTitle": "መለያዎን ማን ማየት ይችላል",
  "signIn.privacy": "የሕንፃዎ ኮሚቴና ሒሳብ ሠራተኛው ብቻ። በልዩ ላይ ያለ ሌላ ሕንፃ ሊያነብበው አይችልም።",
  brandSuffix: "ማኔጅመንት",
  "signIn.home": "ወደ ድረ ገጹ ተመለስ",
  "signIn.eyebrow": "የሕንፃ አስተዳደር",
  "signIn.title": "ወደ ልዩ ይግቡ",
  "signIn.lead": "ነዋሪዎች በሕንፃው ኮድና በቤት ቁጥራቸው ይገባሉ። የኮሚቴ አባላትና ሒሳብ ሠራተኞች የሠራተኛ መግቢያ ይጠቀማሉ።",
  "signIn.orgCode": "የሕንፃ ኮድ",
  "signIn.orgCodeHint": "በተሰጠዎት ወረቀት ላይ ነው",
  "signIn.unit": "ቤት",
  "signIn.unitHint": "ለምሳሌ A-101",
  "signIn.staffId": "የሠራተኛ መለያ",
  "signIn.staffIdHint": "ለምሳሌ committee",
  "signIn.password": "የይለፍ ቃል",
  "signIn.passwordHint": "የይለፍ ቃልዎ",
  "signIn.showPassword": "አሳይ",
  "signIn.hidePassword": "ደብቅ",
  "signIn.submit": "ግባ",
  "signIn.working": "ልዩ በመክፈት ላይ",
  "signIn.checking": "አስቀድመው መግባትዎን በማረጋገጥ ላይ",
  "signIn.session": "ክፍለ ጊዜዎ በዚህ አሳሽ ውስጥ ይቆያል።",
  "signIn.wrongDoor": "ትክክለኛውን የመለያ ዓይነት ይምረጡ፦ ነዋሪ ወይም ሠራተኛ።",
  "signIn.refused": "የሕንፃ ኮዱ፣ የቤት ቁጥሩ ወይም የይለፍ ቃሉ ትክክል አይደለም።",
  "signIn.tooMany": "ብዙ ሙከራ ተደርጓል። አንድ ደቂቃ ቆይተው እንደገና ይሞክሩ።",
  "signIn.offline": "ግንኙነት የለም። አውታረ መረቡን አረጋግጠው እንደገና ይሞክሩ።",
  "reset.forgot": "የይለፍ ቃልዎን ረሱ?",
  "reset.title": "የይለፍ ቃልዎን ይቀይሩ",
  "reset.lead": "ነዋሪዎች፦ ሲዘጋጁ ወዳረጋገጡት ስልክ ኮድ እንልካለን።",
  "reset.staff": "ሠራተኞች፦ መግቢያዎን የመድረክ አስተዳዳሪ ይቀይራል። ሊዩን ይጠይቁ።",
  "reset.phone": "የስልክ ቁጥርዎ",
  "reset.send": "ኮድ ላክ",
  "reset.code": "የማረጋገጫ ኮድ",
  "reset.verify": "ኮዱን አረጋግጥ",
  "reset.back": "ወደ መግቢያ ተመለስ",
  "reset.badPhone": "የስልክ ቁጥርዎን ያስገቡ፤ ለምሳሌ 0911 234 567።",
  "reset.badCode": "ኮዱ ትክክል አይደለም ወይም ጊዜው አልፏል። እንደገና ይሞክሩ።",
  "reset.noAccount": "ይህን ስልክ የሚጠቀም መለያ የለም። ቢሮውን መግቢያዎን እንዲቀይር ይጠይቁ።",
  "reset.done": "የይለፍ ቃሉ ተቀይሯል።",
  "signIn.setupTitle": "የራስዎን የይለፍ ቃል ይምረጡ",
  "signIn.residentSetupLead":
    "የወረቀቱ የይለፍ ቃል አንድ ጊዜ ብቻ ይሠራል። ስምዎን ያስገቡ፣ ስልክዎን በጽሑፍ መልእክት ያረጋግጡ፣ ከዚያ የራስዎን የይለፍ ቃል ይምረጡ።",
  "signIn.residentSetupVouched":
    "ቢሮው በአካል አረጋግጦዎታል፤ የጽሑፍ መልእክት አያስፈልግም። ስምዎንና ስልክ ቁጥርዎን ያስገቡ፣ ከዚያ የራስዎን የይለፍ ቃል ይምረጡ።",
  "signIn.setupName": "ሙሉ ስም",
  "signIn.setupNameNeeded": "ከ2 እስከ 120 ፊደላት ያለው ስም ያስገቡ።",
  "signIn.setupPhone": "ስልክ ቁጥር",
  "signIn.setupPhoneHint": "መለያዎን ለማስመለስና የሕንፃው ሠራተኞች እንዲያገኙዎ። እንደ 0911 234 567 ይጻፉ።",
  "signIn.setupPhoneTaken": "ይህ ቁጥር በሌላ መለያ ላይ ተረጋግጧል። ቢሮውን ይጠይቁ።",
  "signIn.setupLead": "የተሰጠዎት የይለፍ ቃል አንድ ጊዜ ብቻ ይሠራል። ዝግጅቱን ለመጨረስ እርስዎ ብቻ የሚያውቁትን ይምረጡ።",
  "signIn.newPassword": "አዲስ የይለፍ ቃል",
  "signIn.newPasswordAgain": "የይለፍ ቃል ያረጋግጡ",
  "signIn.setupSubmit": "የይለፍ ቃሌን አስቀምጥ",
  "signIn.setupWorking": "በማስቀመጥ ላይ",
  "signIn.setupMismatch": "ሁለቱ የይለፍ ቃሎች የተለያዩ ናቸው።",
  "signIn.setupTooShort": "ቢያንስ 8 ፊደላት ይጠቀሙ።",
  "signIn.setupNeedsLetterAndNumber": "ቢያንስ አንድ ፊደልና አንድ ቁጥር ይጠቀሙ።",

  "home.rights": "ልዩ ማኔጅመንት",
  "home.tagline": "የማኅበረሰብ አስተዳደር መድረክ",

  "statement.export": "መግለጫ አውርድ",
  "statement.exporting": "መግለጫ በማዘጋጀት ላይ",
  "statement.done": "መግለጫው ወርዷል።",
  "statement.hint": "የዚህ ወር ሰንጠረዥ፦ የተጠየቀው፣ የገባው፣ የወጣውና ከኋላቸው ያለው እያንዳንዱ መዝገብ።",
  "statement.title": "ወርሃዊ መግለጫ",
  "statement.building": "ሕንፃ",
  "statement.month": "ወር",
  "statement.preparedBy": "ያዘጋጀው",
  "statement.preparedOn": "የተዘጋጀበት ቀን",
  "statement.summary": "ወርሃዊ ማጠቃለያ",
  "statement.charged": "የተጠየቀ",
  "statement.collected": "የተሰበሰበ",
  "statement.stillOwed": "ቀሪ ዕዳ",
  "statement.spent": "የወጣ",
  "statement.net": "ከገቢ ወጪ ሲቀነስ",
  "statement.held": "ተይዞ ያለ ገንዘብ",
  "statement.costsByKind": "ወጪዎች በምድብ",
  "statement.stillOwing": "ዕዳ ያለባቸው ቤቶች",
  "statement.everyEntry": "የዚህ ወር እያንዳንዱ መዝገብ",
  "statement.kind": "ዓይነት",
  "statement.charge": "ክፍያ ተጠይቋል",
  "statement.payment": "የተከፈለ",
  "statement.where": "የክፍያ ዘዴ",
  "statement.who": "የመዘገበው",
  "statement.note": "ማስታወሻ",
  "statement.corrects": "የሚያርመው መዝገብ",

  "nav.home": "መነሻ",
  "nav.pay": "ክፍያ ይክፈሉ",
  "nav.repairs": "ጥገና",
  "nav.notices": "ማስታወቂያዎች",
  "nav.give": "አስተዋጽኦ",
  "nav.advertisements": "ባለሙያዎች",
  "fix.title": "ማስተካከያዎች",
  "fix.record": "ክፍያ መዝግብ",
  "fix.amount": "መጠን",
  "fix.why": "ምክንያት",
  "fix.whyHint": "ኮሚቴው ይህን ያነባል።",
  "fix.adjust": "ማስተካከያ አስገባ",
  "fix.add": "ዕዳው ላይ ጨምር",
  "fix.reduce": "ዕዳውን ቀንስ",
  "fix.whichMonth": "ወር",
  "fix.voidMonth": "የአንድ ወር ክፍያ ሰርዝ",
  "fix.voidHint": "ይህ ክፍያውን ይመልሳል እንጂ አይሰርዘውም።",
  "fix.changeBilling": "የዚህን ቤት ክፍያ ቀይር",
  "fix.monthlyDue": "ወርሃዊ ክፍያ",
  "fix.paused": "ቆሟል",
  "fix.nextRunOnly": "ከሚቀጥለው ወር ይጀምራል። አስቀድሞ የተከፈለባቸው ወራት አይለወጡም።",
  "fix.save": "አስቀምጥ",
  "fix.takeBack": "ክፍያ መልስ",
  "fix.reverseHint": "ምክንያት ያስገቡ፤ ለምሳሌ፦ ያልተሳካ ዝውውር ወይም ተደጋጋሚ ክፍያ።",
  "fix.post": "ማስተካከያ አስቀምጥ",
  "fix.cancelBill": "ይህን ክፍያ ሰርዝ",
  "held.heading": "የተያዙ ገንዘቦች",
  "held.none": "የሚመደብ ገንዘብ የለም።",
  "held.settle": "ምን እንደሚደረግ ይወስኑ",
  "held.whyHint": "ነዋሪውም ኮሚቴውም ይህን ያነባሉ።",
  "bk.void": "ወጪ ሰርዝ",
  "bk.voided": "ተሰርዟል።",
  "bk.accounts": "ሒሳቦች",
  "bk.recount": "ቀሪውን አድስ",
  "bk.balance": "ወቅታዊ ቀሪ ሒሳብ",
  "bk.neverCounted": "ተቆጥሮ አያውቅም።",
  "bk.noAccounts": "እስካሁን ሒሳብ የለም። ከታች ይጨምሩ።",
  "bk.building": "የክፍያ ቅንብሮች",
  "bk.defaultDue": "ለአዲስ ቤት ወርሃዊ ክፍያ",
  "bk.dueDay": "ክፍያ የሚከፈልበት ቀን",
  "bk.buildingHint": "ለውጦች የሚደርሱት ወደሚቀጥለው ወር ነው፣ አስቀድሞ የተከፈለበትን አይነኩም።",
  "bk.changeBuilding": "የክፍያ ቅንብሮችን አስተካክል",
  "bk.addAccount": "ሒሳብ ጨምር",
  "bk.accountName": "የሒሳብ ስም",
  "held.refunded": "ተመላሽ ገንዘብ መዝግብ",
  "held.waived": "ለሕንፃው አስቀምጥ",
  "held.extended": "ከቀሪ ክፍያ ቀንስ",
  "fix.done": "ተደርጓል።",
  "signInReset.title": "የነዋሪው መግቢያ",
  "signInReset.hint":
    "የይለፍ ቃሉን ለጠፋበትና በስልኩ መቀየር ለማይችል ነዋሪ። አዲስ የአንድ ጊዜ የይለፍ ቃል ያገኛል፤ በሚቀጥለው ሲገባ የራሱን ይመርጣል።",
  "signInReset.button": "አዲስ የአንድ ጊዜ የይለፍ ቃል ስጥ",
  "signInReset.issued": "የ{{unit}} አዲስ የአንድ ጊዜ የይለፍ ቃል። ለነዋሪው በአካል ይስጡ፤ እንደገና አይታይም።",
  "bk.countedOn": "በ{{date}} ተዘምኗል",
  "fix.needAmount": "መጠኑን በሙሉ ብር ያስገቡ።",
  "ads.add": "ባለሙያ ጨምር",
  "ads.edit": "ባለሙያውን አስተካክል",
  "ads.remove": "ባለሙያውን አስወግድ",
  "ads.save": "ባለሙያውን አስቀምጥ",
  "ads.businessName": "ስም (የሰው ወይም የድርጅት)",
  "trade.plumbing": "የቧንቧ ሥራ",
  "trade.electrical": "የኤሌክትሪክ ሥራ",
  "trade.lift": "የሊፍት ጥገና",
  "trade.carpentry": "የእንጨት ሥራ",
  "trade.painting": "ቀለም ቅብ",
  "trade.masonry": "የግንብና የሰድር ሥራ",
  "trade.metalwork": "የብረት ሥራና ብየዳ",
  "trade.cleaning": "ጽዳት",
  "trade.security": "ጥበቃ",
  "trade.pestControl": "የተባይ ማጥፊያ",
  "trade.gardening": "የአትክልት ሥራ",
  "trade.other": "ሌላ",
  "ads.trade": "ሙያ",
  "ads.chooseTrade": "ሙያ ይምረጡ",
  "ads.needTrade": "ምን ዓይነት ሥራ እንደሚሠሩ ይምረጡ።",
  "contractors.allTrades": "ሁሉም ሙያዎች",
  "contractors.search": "ባለሙያ ፈልግ",
  "contractors.searchHint": "ስም",
  "contractors.showing": "ከ{{total}} ባለሙያዎች {{shown}}",
  "contractors.none": "የሚስማማ ባለሙያ የለም።",
  "contractors.noneOfTrade": "የዚህ ሙያ ባለሙያ እስካሁን አልተመዘገበም።",
  "repairs.findWorker": "ባለሙያ ፈልግ",
  "repairs.hideWorkers": "ባለሙያዎችን ደብቅ",
  "ads.description": "መግለጫ",
  "ads.phone": "የስልክ ቁጥር",
  "ads.explain": "በኮሚቴዎ የተመዘገቡ የባለሙያ አገልግሎቶች። ሥራውን ለማስሠራት አቅራቢውን በቀጥታ ያግኙ።",
  "repairs.move.assigned": "በሂደት ላይ አድርግ",
  "repairs.move.attended": "ተጠናቋል በል",
  "repairs.move.closed": "ጥያቄ ዝጋ",
  "repairs.move.reported": "እንደገና ክፈት",
  "repairs.assignedTo": "{{name}} ({{trade}})፣ {{phone}}",
  "ads.saved": "ተቀምጧል።",
  "ads.removed": "ተወግዷል።",
  "ads.bad": "አራቱንም ይሙሉ። ስልኩ እንደ 0911 234 567 ሊጻፍ ይችላል።",
  "repairs.moved": "ተዘምኗል።",
  "repairs.call": "ይደውሉ",
  "repairs.whatsapp": "ዋትስአፕ",
  "repairs.telegram": "ቴሌግራም",
  "repairs.message": "የጥገና ሥራ በ{{org}}፣ ቤት {{unit}}፦ {{what}}። እባክዎ ኮሚቴውን ይደውሉ።",
  "resident.notFixed": "አሁንም አልተስተካከለም",
  "resident.sentBack": "ወደ ኮሚቴው ተመልሷል።",
  "advertisements.none": "ኮሚቴው እስካሁን ምንም ባለሙያ አልዘረዘረም።",
  "nav.queue": "ክፍያዎችን አረጋግጥ",
  "nav.units": "ቤቶች",
  "nav.books": "ፋይናንስ",
  "nav.arrears": "ያልተከፈሉ ክፍያዎች",
  "nav.journal": "የእንቅስቃሴ መዝገብ",
  "nav.overview": "አጠቃላይ እይታ",
  "nav.campaigns": "ገንዘብ ማሰባሰብ",

  "resident.owe": "ዕዳዎ",
  "resident.oweNone": "ምንም ዕዳ የለም",
  "resident.oweMonths_one": "{{count}} ወር ቀሪ",
  "resident.oweMonths_other": "{{count}} ወራት ቀሪ",
  "resident.waiting": "ማረጋገጫ የሚጠብቅ",
  "resident.waitingCount_one": "{{count}} ክፍያ በመረጋገጥ ላይ",
  "resident.waitingCount_other": "{{count}} ክፍያዎች በመረጋገጥ ላይ",
  "resident.openRepairs": "ያልተጠናቀቁ ጥገናዎች",
  "resident.openRepairsSub": "እርስዎን ወይም የጋራ ቦታዎችን የሚመለከት",
  "resident.months": "ወርሃዊ ክፍያዎች",
  "resident.latestNotice": "የቅርብ ማስታወቂያ",
  "resident.paymentReminder": "የክፍያ ማስታወሻ",
  "resident.paymentReminderBody": "በ{{months}} ወር ውስጥ {{amount}} ዕዳ አለ። ለመክፈል ወደ ክፍያ ይሂዱ።",
  "resident.dismissReminder": "ደብቅ",
  "resident.allNotices": "ሁሉም ማስታወቂያዎች",
  "resident.balance": "አሁን ያለብዎት",
  "resident.paymentAmount": "የሚከፈል መጠን (ብር)",
  "queue.customAmount": "የተመረጠ መጠን",
  "resident.reference": "የባንክ ማመሳከሪያ",
  "resident.referenceHint": "በደረሰኝዎ ላይ ያለው የግብይት መለያ",
  "resident.submit": "ክፍያ ላክ",
  "telebirr.notYet": "በቅርቡ ይመጣል። ለአሁን በባንክ ዝውውር ይጠቀሙ።",
  "telebirr.or": "ወይም በቴሌብር ይክፈሉ",
  "telebirr.hint": "ከላይ ያለውን መጠን ከቴሌብር ቦርሳዎ ይከፍላል። ቴሌብር ለሊዩ ሲያረጋግጥ ይቆጠራል፤ ደረሰኝ አያስፈልግም።",
  "telebirr.pay": "በቴሌብር ይክፈሉ",
  "telebirr.title": "የቴሌብር ክፍያዎ",
  "telebirr.waiting": "ቴሌብር ክፍያዎን እስኪያረጋግጥ እየጠበቅን ነው። ሲያረጋግጥ ይህ ገጽ ራሱ ይዘመናል።",
  "telebirr.check": "ቴሌብርን እንደገና ጠይቅ",
  "telebirr.paid": "ቴሌብር {{amount}} አረጋግጧል። በሒሳብዎ ላይ ገብቷል።",
  "telebirr.needsReview": "ቴሌብር ክፍያውን ወስዷል፤ ሒሳብ ሠራተኛው እያጣራው ነው። እንደገና መክፈል አያስፈልግዎትም።",
  "telebirr.failed": "ቴሌብር ክፍያውን መጀመር አልቻለም። ምንም አልተወሰደም።",
  "chapa.or": "ወይም በኦንላይን ይክፈሉ፦ ቴሌብር፣ ሲቢኢ ብር፣ ባንክ ወይም ካርድ",
  "chapa.hint":
    "ከላይ ያለውን መጠን በቻፓ በኩል በቴሌብር፣ በሲቢኢ ብር፣ በባንክዎ ወይም በካርድ ይከፍላል። ቻፓ ለሊዩ ሲያረጋግጥ ይቆጠራል፤ ደረሰኝ አያስፈልግም።",
  "chapa.pay": "በቻፓ ይክፈሉ",
  "chapa.title": "የቻፓ ክፍያዎ",
  "chapa.waiting": "ቻፓ ክፍያዎን እስኪያረጋግጥ እየጠበቅን ነው። ሲያረጋግጥ ይህ ገጽ ራሱ ይታደሳል።",
  "chapa.check": "ቻፓን እንደገና ጠይቅ",
  "chapa.paid": "ቻፓ {{amount}} አረጋግጧል። በሒሳብዎ ላይ ገብቷል።",
  "chapa.needsReview": "ቻፓ ክፍያውን ወስዷል፤ ሒሳብ ሠራተኛው እያጣራው ነው። እንደገና መክፈል አያስፈልግዎትም።",
  "chapa.failed": "ቻፓ ክፍያውን መጀመር አልቻለም። ምንም አልተወሰደም።",
  "nav.chapa": "ቻፓ",
  "chapa.receipts": "የቻፓ ደረሰኞች",
  "chapa.receiptsLead":
    "እነዚህን ክፍያዎች ቻፓ ራሱ አረጋግጧቸዋል፤ ማጣራት አያስፈልጋቸውም፣ ቤቶቹም እንደተከፈሉ ተመዝግበዋል። ቻፓ የወሰደው ነገር ግን ልዩ ማዛመድ ያልቻለው ክፍያ በ«ክፍያዎችን አረጋግጥ» ውስጥ ይጠብቃል።",
  "chapa.money": "በቻፓ የተከፈለ ገንዘብ",
  "chapa.moneyLead":
    "ቻፓ እያንዳንዱን ክፍያ ነዋሪው ከከፈለ በኋላ በ24 ሰዓት ገደማ ወደ ኮሚቴው የባንክ ሒሳብ ይልካል። እነዚህ አኃዞች ቻፓ ያረጋገጣቸው ክፍያዎች ናቸው፤ የመጨረሻው ማረጋገጫ የባንክ መግለጫው ነው።",
  "chapa.onTheWay": "ወደ ባንክ በመሄድ ላይ",
  "chapa.reached": "በኮሚቴው ባንክ ገብቷል",
  "chapa.total": "በቻፓ የተከፈለ",
  "chapa.count_one": "{{count}} ክፍያ",
  "chapa.count_other": "{{count}} ክፍያዎች",
  "chapa.lastArrival": "ሁሉም እስከ {{when}} ገደማ ባንክ ይገባል",
  "chapa.arrivesBy": "በመሄድ ላይ፤ እስከ {{when}} ገደማ ባንክ ይገባል",
  "chapa.inBank": "ባንክ ገብቷል",
  "chapa.paidAt": "የተከፈለበት",
  "chapa.where": "ገንዘቡ ያለበት",
  "chapa.status": "ሁኔታ",
  "chapa.verified": "ቻፓ አረጋግጧል",
  "chapa.submitted": "በ«ክፍያዎችን አረጋግጥ» ውስጥ ይጠብቃል",
  "chapa.rejected": "ውድቅ ተደርጓል",
  "chapa.none": "እስካሁን በቻፓ የከፈለ የለም።",
  "chapa.receiptOpen": "ደረሰኙን ክፈት",
  "chapa.receiptTitle": "የቻፓ ደረሰኝ",
  "chapa.receiptLoading": "ይህን ደረሰኝ ከቻፓ እየጠየቅን ነው…",
  "chapa.receiptClose": "ደረሰኙን ዝጋ",
  "chapa.receiptMethod": "የተከፈለበት መንገድ",
  "chapa.receiptPayer": "የከፈለው",
  "chapa.receiptCovers": "የተከፈለለት",
  "chapa.receiptHeld": "{{amount}} እርስዎ እንዲወስኑበት ተይዟል",
  "chapa.receiptChapaRef": "የቻፓ ማመሳከሪያ",
  "chapa.receiptLiyuRef": "የልዩ ማመሳከሪያ",
  "chapa.receiptChapaSays": "ቻፓ የሚለው",
  "chapa.receiptConfirmed": "ተከፍሏል፤ መጠኑም ይዛመዳል",
  "chapa.receiptMismatch": "ቻፓ {{status}}፣ {{amount}} ይላል። ከመጠቀምዎ በፊት ይህን ክፍያ በቻፓ ዳሽቦርድ ያረጋግጡ።",
  "chapa.receiptChecked": "ከቻፓ ጋር የተረጋገጠው {{when}}",
  "chapaReturn.title": "ወደ ልዩ ተመለሱ",
  "chapaReturn.lead": "ክፍያዎ ከቻፓ ጋር የሚረጋገጥበትን የልዩ መተግበሪያ እየከፈትን ነው። በራሱ ካልተከፈተ ቁልፉን ይጫኑ።",
  "chapaReturn.open": "የልዩ መተግበሪያን ክፈት",
  "chapaReturn.noOrder": "ይህ ማስፈንጠሪያ ክፍያውን አይገልጽም። ክፍያዎን ለማየት የልዩ መተግበሪያን ከፍተው «ክፍያ ይክፈሉ» ይግቡ።",
  "footer.runBy": "ልዩ በNADAWI Technologies የሚሠራ ነው",
  "home.receiptNo": "ቁጥር",
  "home.currency": "ብር",
  "home.unpaid": "{{months}} አልተከፈለም",
  "home.withAccountant": "ሒሳብ ሠራተኛው {{amount}} እያረጋገጠ ነው",
  "home.pay": "{{amount}} ይክፈሉ",
  "home.payFees": "ክፍያ ይክፈሉ",
  "home.methods": "ቴሌብር · ሲቢኢ ብር · ባንክ · ካርድ",
  "home.year": "{{year}} ዓ.ም.",
  "home.yearSub": "ዓመትዎ በየወሩ",
  "home.notDue": "ገና አልደረሰም",
  "home.now": "የአሁኑ",
  "home.earlier": "ያልተከፈሉ የቀድሞ ወራት",
  "home.repairs": "ጥገና · {{count}} ክፍት",
  "home.noNotices": "ኮሚቴው እስካሁን ምንም አልለጠፈም።",
  "home.notice": "የቅርብ ማስታወቂያ",
  "home.history": "ያለፉ ክፍያዎች",
  "home.historySub": "የላኩት እና ሒሳብ ሠራተኛው የወሰነው",
  "pay.receipt": "ደረሰኝ",
  "pay.from": "ከ",
  "pay.amount": "መጠን",
  "pay.for": "ለወራት",
  "pay.to": "ለ",
  "pay.unit": "ቤት {{unit}}",
  "pay.oldest": "{{month}} ብቻ · {{amount}}",
  "pay.all": "ሙሉውን · {{amount}}",
  "pay.nothingOwed": "አሁን ዕዳ የለብዎትም፤ ክፍያው ለሒሳብ ሠራተኛው ይያዛል።",
  "pay.held": "እና {{amount}} ለሒሳብ ሠራተኛው ይያዛል",
  "pay.withChapa": "{{amount}} በቻፓ ይክፈሉ",
  "pay.aheadOne": "እና የሚቀጥለው ወር · {{amount}}",
  "pay.aheadMany": "እና {{count}} ወራት አስቀድመው · {{amount}}",
  "pay.anyAmount": "ወይም ማንኛውንም መጠን በሙሉ ብር ያስገቡ። ካለብዎት በላይ የሆነው ለሒሳብ ሠራተኛው ይያዛል።",
  "paid.title": "ክፍያው ተረጋግጧል",
  "paid.body": "ቻፓ {{amount}} አረጋግጧል። በሒሳብዎ ላይ ገብቷል፤ ለዚህ ዳግም መክፈል አያስፈልግም።",
  "paid.leaving": "ወደ መነሻ ገጽ እየወሰድንዎ ነው…",
  "paid.home": "ወደ መነሻ ገጽ",
  "history.title": "የክፍያ ታሪክ",
  "history.lead": "አዲሱ መጀመሪያ። የላኩት እያንዳንዱ ክፍያ እና የደረሰበት።",
  "history.open": "የክፍያ ታሪክ ይዩ",
  "history.none": "እስካሁን ክፍያ አልላኩም።",
  "history.covered": "{{months}} ሸፍኗል።",
  "history.waiting": "ማረጋገጫ ይጠብቃል። ሒሳብ ሠራተኛው ክፍያውን ሲያረጋግጥ ቀሪ ሒሳብዎ ይዘምናል።",
  "history.viaChapa": "በቻፓ የተከፈለ",
  "history.viaBank": "የባንክ ዝውውር",
  "history.towards": "ለ{{title}}",
  "history.whyTurnedDown": "ለምን ተመለሰ",
  "history.sendAgain": "እንደገና ላክ",
  "history.extra.unresolved":
    "ከዚህ ውስጥ {{amount}} ካለብዎት በላይ ነው። ሒሳብ ሠራተኛው ይመልሰዋል፣ ለክፍያዎ ያውለዋል ወይም ለሕንጻው ያስቀምጠዋል።",
  "history.extra.refunded": "ከዚህ ውስጥ {{amount}} ካለብዎት በላይ ነበር። ሒሳብ ሠራተኛው እንደተመለሰልዎ መዝግቧል።",
  "history.extra.waived": "ከዚህ ውስጥ {{amount}} ካለብዎት በላይ ነበር። ሒሳብ ሠራተኛው ለሕንጻው ገንዘብ አስቀምጦታል።",
  "history.extra.extended": "ከዚህ ውስጥ {{amount}} ካለብዎት በላይ ነበር። ሒሳብ ሠራተኛው ለ{{month}} ክፍያዎ አውሎታል።",
  "pay.bankTitle": "በባንክ ዝውውር ከፍለዋል?",
  "pay.bankLead": "ማመሳከሪያውን እና የደረሰኙን ፎቶ ይላኩ። ሒሳብ ሠራተኛው ካረጋገጠ በኋላ ይቆጠራል።",
  "resident.partialPaymentHint":
    "ማንኛውንም የሙሉ ብር መጠን ይክፈሉ። ሒሳብ ሠራተኛው ካረጋገጠ በኋላ ከቆየው ያልተከፈለ ክፍያ ይቀነሳል። ከዕዳዎ በላይ የሆነው ለግምገማ ይቀመጣል።",
  "resident.invalidPaymentAmount": "ቢያንስ 1 ብር የሆነ ሙሉ ቁጥር ያስገቡ።",
  "resident.sent": "ተልኳል። ሒሳብ ሠራተኛው ያረጋግጠዋል።",
  "resident.receipt": "የደረሰኝ ፎቶ",
  "resident.receiptHint": "የዝውውሩ ፎቶ ወይም ቅጽበታዊ ገጽ እይታ፣ ከ10 ሜባ በታች።",
  "resident.needReceipt": "የዝውውሩን ደረሰኝ ፎቶ ያክሉ።",
  "resident.needReference": "በደረሰኙ ላይ እንዳለው የዝውውሩን ማመሳከሪያ ያስገቡ።",
  "resident.confirmFixed": "ጥገና አረጋግጥ",
  "resident.confirmedFixed": "እናመሰግናለን። ኮሚቴው አሁን ሊዘጋው ይችላል።",
  "resident.newRepair": "ጥገና ያሳውቁ",
  "resident.whatBroke": "ችግር",
  "resident.whatBrokeHint": "ለምሳሌ የኩሽና ውኃ ማፍሰስ",
  "resident.raise": "ጥያቄ ላክ",
  "resident.yourRequests": "የእርስዎ ጥያቄዎች",
  "resident.needTitle": "ምን እንደተበላሸ ይግለጹ።",
  "resident.raised": "ለኮሚቴው ተልኳል።",
  "resident.giveOpen": "ክፍት ማሰባሰብ",
  "resident.giveAmount": "መጠን",
  "resident.giveSend": "መዋጮ ላክ",
  "resident.giveHint": "ሒሳብ ሠራተኛው ካረጋገጠ በኋላ ወደ ድምሩ ይታከላል።",
  "resident.giveAmountBad": "መጠኑን በሙሉ ብር ያስገቡ።",
  "resident.gaveIt": "ተልኳል። ሒሳብ ሠራተኛው ያረጋግጠዋል።",
  "resident.raisedOf": "ከ{{target}} ውስጥ {{raised}}",

  "queue.waiting": "በመጠባበቅ ላይ",
  "queue.waitingSub": "በአጠቃላይ {{amount}}",
  "queue.held": "የተያዙ ገንዘቦች",
  "queue.heldSub": "ውሳኔ የሚጠብቅ",
  "queue.collected": "ተከፍሎ የተረጋገጠ",
  "queue.collectedSub": "በመዝገብ ውስጥ የተያዘ",
  "queue.heading": "የሚረጋገጡ ክፍያዎች",
  "queue.clear": "ማረጋገጫ የሚጠብቅ ክፍያ የለም",
  "queue.clearSub": "ሁሉም ክፍያ ተረጋግጧል።",
  "queue.unit": "ቤት",
  "queue.review": "ዝርዝሩን መርምር",
  "queue.hideReview": "ዝርዝሩን ደብቅ",
  "queue.paymentId": "የክፍያ መለያ",
  "queue.block": "ብሎክ",
  "queue.campaign": "ዘመቻ",
  "queue.noReceipt": "ደረሰኝ አልተያያዘም",
  "queue.months": "ወራት",
  "queue.amount": "መጠን",
  "queue.reference": "ማመሳከሪያ",
  "queue.when": "የተላከበት",
  "queue.verify": "ክፍያውን አረጋግጥ",
  "queue.reject": "ውድቅ አድርግ",
  "queue.verified": "ተረጋግጦ ተያዘ።",
  "queue.rejected": "ውድቅ ተደርጓል። ነዋሪው እንደገና መላክ ይችላል።",
  "queue.why": "ያልተቀበለበት ምክንያት",
  "queue.needWhy": "ነዋሪው ምን ማስተካከል እንዳለበት እንዲያውቅ ምክንያቱን ይግለጹ።",

  "units.heading": "ቤቶች",
  "units.sub": "በብሎክ የተከፋፈሉ ሒሳቦች",
  "units.all": "ሁሉም",
  "units.block": "ብሎክ {{block}}",
  "units.blockColumn": "ብሎክ",
  "units.current": "የተሟሉ",
  "units.status": "ሁኔታ",
  "units.balance": "ዕዳ",
  "units.allBlocks": "ሁሉም ብሎኮች",
  "units.filterStatus": "ክፍያ",
  "units.search": "ቤት ፈልግ",
  "units.searchHint": "ለምሳሌ GA01",
  "units.sort": "ቅደም ተከተል",
  "units.sortUnit": "በቤት ቁጥር",
  "units.sortOwed": "ብዙ ዕዳ ያለባቸው መጀመሪያ",
  "units.showing": "ከ{{total}} ቤቶች {{shown}} · {{owed}} ዕዳ",
  "units.clear": "ማጣሪያዎችን አጽዳ",
  "units.noMatch": "ከእነዚህ ማጣሪያዎች ጋር የሚስማማ ቤት የለም።",
  "units.details": "የቤቱ ዝርዝር",
  "units.closeDetails": "ዝርዝሩን ዝጋ",
  "units.paymentHistory": "የክፍያ ታሪክ",
  "units.noPayments": "ለዚህ ቤት እስካሁን የተመዘገበ ክፍያ የለም።",
  "units.paymentStatus": "የክፍያ ሁኔታ",
  "units.paymentReference": "ማጣቀሻ",
  "units.paymentDate": "የቀረበበት",
  "units.receipt": "ደረሰኙን አሳይ",
  "units.receiptLoading": "ደረሰኙ በመክፈት ላይ…",
  "units.receiptUnavailable": "ደረሰኙ አልተገኘም",
  "units.zoomOut": "አሳንስ",
  "units.zoomReset": "100％",
  "units.zoomIn": "＋",
  "units.fullscreen": "ሙሉ ማያ",
  "units.closeReceipt": "ዝጋ",

  "books.collected": "ተከፍሎ የተረጋገጠ",
  "books.collectedSub": "በመዝገብ ውስጥ የተያዘ",
  "books.spent": "ወጪ",
  "books.spentSub": "የተመዘገበ ወጪ",
  "books.net": "ቀሪ",
  "books.netSub": "ገቢ ሲቀነስ ወጪ",
  "books.outstanding": "ያልተከፈለ",
  "books.outstandingSub": "በሁሉም ቤቶች",
  "books.record": "ወጪ መዝግብ",
  "books.amount": "መጠን",
  "books.category": "ምድብ",
  "books.description": "መግለጫ",
  "books.descriptionHint": "ኮሚቴው ይህንን ያነብባል።",
  "books.vendor": "የተከፈለው ለ",
  "books.vendorHint": "ሱቁ፣ ተቋራጩ፣ ጽሕፈት ቤቱ።",
  "books.add": "ወጪ አስቀምጥ",
  "books.recent": "የቅርብ ጊዜ ወጪዎች",
  "books.date": "ቀን",
  "books.needAmount": "መጠኑን በሙሉ ብር ያስገቡ።",
  "books.needDescription": "ለምን እንደዋለ ይግለጹ።",
  "books.needVendor": "ለማን እንደተከፈለ ይግለጹ።",
  "books.recorded": "ወጪው ተመዝግቧል።",

  "arrears.behind": "ወደኋላ የቀሩ ቤቶች",
  "arrears.total": "በአጠቃላይ ያለ ዕዳ",
  "arrears.inQueue": "ክፍያዎችን አረጋግጥ",
  "arrears.inQueueSub": "የተወሰነውን ሊያጠፋ ይችላል",
  "arrears.worstFirst": "ብዙ ዕዳ ያለበት መጀመሪያ",
  "arrears.remind": "አስታዋሽ ላክ",
  "arrears.reminded": "አስታዋሽ ተልኳል።",
  "arrears.remindTitle": "ነዋሪዎችን አሳስብ",
  "arrears.remindExplain": "እያንዳንዱ ነዋሪ ያለበትን ዕዳ የሚገልጽ ማሳወቂያ በስልኩና በድረ-ገጹ የልዩ ማሳወቂያ ሳጥን ይደርሰዋል።",
  "arrears.note": "መልእክት ያክሉ (አማራጭ)",
  "arrears.noteHint": "ለምሳሌ፦ ቢሮው ቅዳሜ ጠዋት ለጥሬ ገንዘብ ክፍያ ክፍት ነው።",
  "arrears.remindAll": "ወደኋላ የቀሩትን {{count}} ቤቶች አሳስብ",
  "arrears.confirmAll": "ለ{{count}} ቤቶች አስታዋሽ ይላክ?",
  "arrears.remindedAll": "ለ{{count}} ቤቶች አስታዋሽ ተልኳል።",
  "arrears.oweLine": "{{amount}} ለ{{count}} ወር ያልተከፈለ።",
  "inbox.title": "ማሳወቂያዎች",
  "inbox.unread": "{{count}} ያልተነበቡ",
  "inbox.none": "እስካሁን ምንም የለም። ከሕንፃዎ የሚመጡ አስታዋሾችና መረጃዎች እዚህ ይታያሉ።",
  "inbox.new": "አዲስ",
  "inbox.back": "ሁሉም ማሳወቂያዎች",
  "inbox.missing": "ይህ ማሳወቂያ ከእንግዲህ የለም።",
  "arrears.none": "ማንም ወደኋላ አልቀረም።",

  "overview.collected": "ተከፍሎ የተረጋገጠ",
  "analytics.outstanding": "በዚህ ወር ያልተከፈለ",
  "analytics.open": "ዝርዝር ይመልከቱ",
  "analytics.choose": "የሒሳብ ምድቦች",
  "analytics.allMonths": "የሁሉም ወራት",
  "analytics.collectedHint": "በየወሩ የተረጋገጡ ክፍያዎች።",
  "analytics.outstandingHint": "በየወሩ የተጠየቁ ግን ያልተከፈሉ ክፍያዎች።",
  "analytics.netHint": "የዚህ ወር የተረጋገጠ ክፍያ ሲቀነስ የዚህ ወር ወጪ።",
  "analytics.spentHint": "የዚህ ወር ወጪዎች በምድብ። የተሰረዙ ወጪዎች አልተካተቱም።",
  "analytics.arrearsHint": "የእያንዳንዱ ቤት ጠቅላላ ዕዳ፣ በቆየው ያልተከፈለ ወር የተመደበ።",
  "analytics.overMonths": "በየወሩ",
  "analytics.whatOn": "ወጪ በምድብ",
  "analytics.inAndOut": "የዚህ ወር ገቢና ወጪ",
  "analytics.howLate": "የክፍያ መዘግየት",
  "analytics.current": "ይህ ወር",
  "analytics.one": "ከ1 ወር በፊት",
  "analytics.two": "ከ2 ወር በፊት",
  "analytics.threePlus": "ከ3 ወር ወይም ከዚያ በላይ",
  "analytics.empty": "እስካሁን የሚታይ መጠን የለም።",
  "analytics.billedVerified": "የተጠየቀና የተረጋገጠ፣ በየወሩ",
  "analytics.billedOutstanding": "የተጠየቀና ያልተከፈለ፣ በየወሩ",
  "analytics.verified": "ተከፍሎ የተረጋገጠ",
  "analytics.outstandingNote": "ያልተከፈለው በሒሳብ ሹሙ ገና ያልተረጋገጡ ክፍያዎችንም ያካትታል።",
  "analytics.unpaid": "ያልተከፈለ",
  "analytics.soFar": "እስካሁን",
  "analytics.columnLine": "{{month}}፦ {{billed}} ተጠይቋል፣ {{verified}} ተረጋግጧል፣ {{rate}} ተከፍሏል",
  "analytics.byBlock": "በብሎክ፣ ዝቅተኛ ክፍያ ያለው መጀመሪያ",
  "analytics.blockLine": "{{percent}} ተከፍሏል",
  "analytics.whereWent": "የዚህ ወር ክፍያ የሄደበት",
  "analytics.stepIn": "የተረጋገጠ ክፍያ",
  "analytics.stepNet": "የቀረ",
  "analytics.stepShort": "የጎደለ",
  "analytics.netMonths": "ቀሪ ገቢ፣ በየወሩ",
  "analytics.loss": "ኪሳራ የነበረበት ወር",
  "analytics.more": "ካለፈው ወር {{amount}} ይበልጣል",
  "analytics.less": "ካለፈው ወር {{amount}} ያንሳል",
  "analytics.same": "ካለፈው ወር ጋር እኩል",
  "analytics.new": "ባለፈው ወር አልነበረም",
  "analytics.owingMost": "ብዙ ዕዳ ያለባቸው ቤቶች",
  "analytics.monthsOwed_one": "የ{{count}} ወር ዕዳ",
  "analytics.monthsOwed_other": "የ{{count}} ወራት ዕዳ",
  "analytics.unitsCount_one": "{{count}} ቤት",
  "analytics.unitsCount_other": "{{count}} ቤቶች",
  "analytics.figures": "አሃዞቹን አሳይ",
  "analytics.month": "ወር",
  "analytics.billed": "የተጠየቀ",
  "analytics.rate": "የተከፈለ",
  "analytics.spentCol": "ወጪ",
  "analytics.netCol": "ቀሪ",
  "analytics.average": "በተጠናቀቁ ወራት አማካይ የተረጋገጠ ክፍያ፦ {{amount}}",
  "analytics.surplus": "የተረፈ ገንዘብ",
  "overview.spent": "ወጪ",
  "overview.net": "ቀሪ",
  "overview.arrears": "በአጠቃላይ ያለ ዕዳ",
  "overview.arrearsSub": "{{unpaid}} ያልተከፈለ፣ {{pending}} በመረጋገጥ ላይ",
  "overview.rate": "የክፍያ አሰባሰብ መጠን",
  "overview.rateRow": "ከተጠየቀው ክፍያ የተሰበሰበ",
  "overview.attention": "ትኩረት የሚፈልጉ",
  "overview.queueRow": "የሚረጋገጡ ክፍያዎች",
  "overview.queueSub": "ሒሳብ ሠራተኛውን የሚጠብቅ",
  "overview.repairsRow": "ያልተጠናቀቁ ጥገናዎች",
  "overview.repairsSub": "የሕንፃ ችግሮች",
  "overview.heldRow": "የተያዙ ገንዘቦች",
  "overview.heldSub": "ውሳኔ የሚጠብቅ",
  "overview.byBlock": "በብሎክ",
  "overview.blockLine": "{{rate}}% ተከፍሏል፣ {{owed}} ቀሪ",

  "campaigns.start": "የገንዘብ ማሰባሰቢያ ፍጠር",
  "campaigns.what": "የገንዘብ ማሰባሰቢያ ስም",
  "campaigns.whatHint": "ለምሳሌ የውኃ ማጠራቀሚያ",
  "campaigns.target": "ግብ",
  "campaigns.begin": "የገንዘብ ማሰባሰቢያ ፍጠር",
  "campaigns.all": "ሁሉም ማሰባሰቦች",
  "campaigns.close": "የገንዘብ ማሰባሰቢያ ዝጋ",
  "campaigns.needBoth": "ስምና ግብ ሁለቱም ያስፈልጋሉ።",
  "campaigns.started": "ማሰባሰቡ ተጀምሯል።",
  "campaigns.wasClosed": "ማሰባሰቡ ተዘግቷል።",

  "bk.autoReminders": "ዕዳ ያለባቸውን ክፍሎች ራሱ ያስታውስ",
  "bk.autoRemindersHint":
    "በወር አንድ ጊዜ፣ ከመክፈያው ቀን {{days}} ቀን በኋላ፣ ዕዳ ያለበት እያንዳንዱ ክፍል በስልኩ ማሳሰቢያ ይደርሰዋል። ቀጣዩ፦ {{next}}።",
  "occupancy.title": "የነዋሪ መለያ",
  "occupancy.noAccount": "ለዚህ ክፍል ገና መግቢያ የለም። ከአስተዳዳሪ ገጹ ይፍጠሩ።",
  "occupancy.setUp": "ተዘጋጅቷል",
  "occupancy.waiting": "የመጀመሪያ መግቢያን እየጠበቀ",
  "occupancy.switchedOff": "ጠፍቷል",
  "occupancy.switchOff": "መለያ አቦዝን",
  "occupancy.switchOn": "መለያ አንቃ",
  "occupancy.switchOffWhy": "ይህ መለያ ለምን ይጠፋል? መግባት ያቆማል፤ የጻፈው ምንም አይሰረዝም።",
  "occupancy.switchOnWhy": "ይህ መለያ ለምን እንደገና ይበራል?",
  "occupancy.handOver": "ነዋሪ ቀይር",
  "occupancy.handOverHint":
    "ክፍሉ እጅ ሲቀየር። አዲሱ ነዋሪ የአንድ ጊዜ የይለፍ ቃል ያገኛል፤ የቀደመውን ነዋሪ ክፍያዎችና ደረሰኞች ማየት አይችልም። የክፍሉ ዕዳ ከክፍሉ ጋር ይቆያል።",
  "occupancy.handOverWhyHint": "አዲስ ተከራይ ከጥቅምት 1፣ ውል ተመዝግቧል",
  "occupancy.handOverButton": "ነዋሪ ቀይር",
  "occupancy.handOverConfirm": "{{unit}}ን ለአዲስ ነዋሪ ላስረክብ? የአሁኑ ነዋሪ መግቢያ መሥራት ያቆማል።",
  "occupancy.handedOver": "ለ{{unit}} አዲስ ነዋሪ የአንድ ጊዜ የይለፍ ቃል። በአካል ይስጡ፤ እንደገና አይታይም።",
  "occupancy.carried": "የቀደመው ነዋሪነት {{amount}} ዕዳ ትቷል፤ ከክፍሉ ጋር ይቆያል።",
  "journal.automatic": "ራስ-ሰር",
  "notices.publish": "ማስታወቂያ ለጥፍ",
  "notices.noticeTitle": "ርዕስ",
  "notices.message": "መልእክት",
  "notices.post": "ማስታወቂያ ለጥፍ",
  "notices.posted": "ተለጥፏል።",
  "notices.published": "የተለጠፉ ማስታወቂያዎች",
  "notices.needBoth": "ርዕስና መልእክት ሁለቱም ያስፈልጋሉ።",
  "notices.keepUp": "የማሳያ ጊዜ",
  "notices.untilRemoved": "እስኪነሳ ድረስ",
  "notices.forDays_one": "ለ{{count}} ቀን",
  "notices.forDays_other": "ለ{{count}} ቀናት",
  "notices.urgent": "አስቸኳይ",
  "notices.urgentHint": "አስቸኳይ ማስታወቂያዎች ከላይ ይቀመጣሉ፤ ለእያንዳንዱ ነዋሪ ስልክም ይላካሉ።",
  "notices.urgentMark": "አስቸኳይ",
  "notices.until": "እስከ {{date}}",
  "notices.remove": "አንሳ",
  "notices.removeConfirm": "ይህን ማስታወቂያ ለሁሉም ላንሳው?",
  "nav.chat": "ውይይት",
  "chat.actionFailed": "ማስቀመጥ አልተቻለም። ግንኙነትዎን አረጋግጠው እንደገና ይሞክሩ።",
  "chat.clearName": "ስሙን ወደ {{unit}} መልስ",
  "chat.committee": "ኮሚቴ",
  "chat.committeeOnly": "ኮሚቴው ብቻ",
  "chat.cooldown": "በመልዕክቶች መካከል የሚጠበቅ ጊዜ",
  "chat.cooldownHint": "ነዋሪዎች እያንዳንዱን መልዕክት ከላኩ በኋላ ይህን ያህል ይጠብቃሉ። ኮሚቴው አይጠብቅም።",
  "chat.cooldownNone": "ጥበቃ የለም",
  "chat.deleteConfirm": "ይህ መልዕክት ለሁሉም ይሰረዝ? መልሶ ማምጣት አይቻልም።",
  "chat.deleteMessage": "መልዕክቱን ሰርዝ",
  "chat.done": "ቅንብሮቹን ዝጋ",
  "chat.everyone": "ሁሉም",
  "chat.forADay": "ለ1 ቀን",
  "chat.forAnHour": "ለ1 ሰዓት",
  "chat.forAWeek": "ለ1 ሳምንት",
  "chat.isOff": "ኮሚቴው መልሶ እስኪያበራው ድረስ ውይይቱ ጠፍቷል።",
  "chat.isOffUntil": "ውይይቱ እስከ {{time}} ድረስ ጠፍቷል።",
  "chat.isOn": "ውይይቱ በርቷል።",
  "chat.locked": "አሁን መልዕክት መላክ የሚችለው ኮሚቴው ብቻ ነው። ውይይቱን ማንበብ ይችላሉ።",
  "chat.lockedCommittee": "አሁን መልዕክት መላክ የሚችለው ኮሚቴው ብቻ ነው።",
  "chat.manage": "የውይይት ቅንብሮች",
  "chat.manageName": "{{name}}ን አስተዳድር",
  "chat.messageActions": "መልዕክት",
  "chat.messageActionsHint": "መሰካት፣ መሰረዝና የላኪውን ውይይት ይከፍታል",
  "chat.minutes": "{{count}} ደቂቃ",
  "chat.nameHintCommittee": "ባዶ ከተዉት ኮሚቴ ተብሎ ይታያል።",
  "chat.nameHintResident": "ባዶ ከተዉት {{unit}} ተብሎ ይታያል። የቤትዎ ቁጥር ሁልጊዜ ከስምዎ አጠገብ ይታያል።",
  "chat.nameLabel": "በውይይቱ ላይ የሚታይ ስም",
  "chat.nobodyYet": "እስካሁን ውይይቱን የከፈተ ነዋሪ የለም።",
  "chat.noFundraisers": "ክፍት የገንዘብ ማሰባሰቢያ የለም።",
  "chat.none": "እስካሁን መልዕክት የለም። ለሕንፃው ሰላምታ ያቅርቡ።",
  "chat.off": "ኮሚቴው ውይይትዎን አጥፍቷል። አሁንም ማንበብ ይችላሉ።",
  "chat.offUntil": "ኮሚቴው ውይይትዎን እስከ {{time}} ድረስ አጥፍቷል። አሁንም ማንበብ ይችላሉ።",
  "chat.people": "በውይይቱ ውስጥ ያሉ ሰዎች",
  "chat.peopleHint": "ውይይቱን የከፈቱ ሁሉ። ማንም አይወገድም፤ አሁንም እዚህ ይኖራሉ። ውይይታቸውን ግን ማጥፋት ይቻላል።",
  "chat.pinFundraisers": "በውይይቱ ላይ የተሰኩ የገንዘብ ማሰባሰቢያዎች",
  "chat.pinFundraisersHint": "እስከ {{count}}። ነዋሪዎች ለማዋጣት አንዱን ይነካሉ።",
  "chat.pinMessage": "መልዕክቱን ሰካ",
  "chat.pinned": "የተሰካ",
  "chat.placeholder": "ለሕንፃው መልዕክት ይጻፉ",
  "chat.problem": "ውይይቱን መጫን አልተቻለም። ግንኙነትዎን ያረጋግጡ።",
  "chat.rename": "ስምዎን ይቀይሩ",
  "chat.saveName": "ስም አስቀምጥ",
  "chat.seconds": "{{count}} ሰከንድ",
  "chat.send": "ላክ",
  "chat.sendFailed": "መላክ አልተቻለም። ትንሽ ቆይተው፣ ግንኙነትዎን አረጋግጠው እንደገና ይሞክሩ።",
  "chat.turnOff": "ውይይቱን አጥፋ",
  "chat.turnOffHint": "አሁንም ውይይቱን ማንበብ ይችላሉ። እስኪበራ ድረስ መልዕክት መላክ አይችሉም።",
  "chat.turnOn": "ውይይቱን መልሶ አብራ",
  "chat.unpinMessage": "መልዕክቱን ንቀል",
  "chat.untilTurnedOn": "መልሶ እስኪበራ ድረስ",
  "chat.wait": "ቀጣዩን መልዕክት በ{{count}} ሰከንድ ውስጥ መላክ ይችላሉ።",
  "chat.whoPosts": "መልዕክት መላክ የሚችለው ማን ነው",
  "chat.whoPostsHint": "«ኮሚቴው ብቻ» ውይይቱ ጸጥ ማለት ሲኖርበት፣ ለምሳሌ በአደጋ ጊዜ ነው። ነዋሪዎች አሁንም ማንበብ ይችላሉ።",
  "chat.you": "እርስዎ",
  "chat.yourName": "ስምዎ፦ {{name}}",
  "chat.close": "ዝጋ",
  "chat.cancel": "ተው",
  "chat.unread": "{{count}} አዲስ",
  "chat.sendHint": "Enter ይልካል። Shift እና Enter አዲስ መስመር ይጀምራል።",

  "repairs.reported": "ተነግሯል",
  "repairs.assigned": "ባለሙያ ተመድቧል",
  "repairs.attended": "የነዋሪውን ማረጋገጫ ይጠብቃል",
  "repairs.confirmed": "መጠገኑ ተረጋግጧል",
  "repairs.closed": "ተዘግቷል",

  /* The change log's own names, and the kinds a repair can be, taken
     verbatim from the phone so one action is not called two things. */
  "action.account.active": "መለያ በርቷል ወይም ጠፍቷል",
  "action.account.reconciled": "የባንክ ቀሪ ተቆጥሯል",
  "action.account.reset": "የይለፍ ቃል ዳግም ተጀምሯል",
  "action.account.setUp": "መለያ ተዘጋጅቷል",
  "action.account.slipsIssued": "የመግቢያ ወረቀቶች ተሰጥተዋል",
  "action.units.added": "ቤቶች ተጨምረዋል",
  "action.account.vouched": "ነዋሪው በቢሮ ተረጋግጧል",
  "action.building.changed": "የሕንፃ ቅንብር ተቀይሯል",
  "action.campaign.closed": "ዘመቻ ተዘግቷል",
  "action.campaign.created": "ዘመቻ ተጀምሯል",
  "action.contribution.reversed": "መዋጮ ተመልሷል",
  "action.contribution.verified": "መዋጮ ተረጋግጧል",
  "action.expense.recorded": "ወጪ ተመዝግቧል",
  "action.expense.voided": "ወጪ ተሰርዟል",
  "action.heldFunds.resolved": "የተያዘ ገንዘብ ተመድቧል",
  "action.invoice.voided": "ደረሰኝ ተሰርዟል",
  "action.invoices.raised": "ደረሰኞች ተዘጋጅተዋል",
  "action.ledger.adjusted": "መዝገብ ተስተካክሏል",
  "action.organization.created": "ሕንፃ ተጨምሯል",
  "action.organization.status": "የሕንፃ ሁኔታ ተቀይሯል",
  "action.subscription.set": "የሊዩ ዕቅድ ተቀይሯል",
  "action.subscription.planChanged": "የሊዩ ዕቅዱ ውሎች ተቀይረዋል",
  "action.telebirr.configured": "የቴሌብር መለያ ተቀይሯል",
  "action.chapa.configured": "የቻፓ ንዑስ መለያ ተቀይሯል",
  "action.payment.recorded": "ክፍያ በእጅ ተመዝግቧል",
  "action.payment.rejected": "ክፍያ አልተቀበለም",
  "action.payment.reversed": "ክፍያ ተመልሷል",
  "action.payment.verified": "ክፍያ ተረጋግጧል",
  "action.reminders.sent": "ማስታወሻዎች ተልከዋል",
  "action.staff.created": "የሠራተኛ መለያ ተፈጥሯል",
  "action.unit.changed": "ቤት ተቀይሯል",
  "action.unit.handover": "ቤት ተላልፏል",
  "category.cleaning": "ጽዳት",
  "category.electrical": "ኤሌክትሪክ",
  "category.government": "የመንግሥት ክፍያ",
  "category.lift": "አሳንሰር",
  "category.maintenance": "ጥገና",
  "category.other": "ሌላ",
  "category.plumbing": "ውሃ",
  "category.salaries": "ደመወዝ",
  "category.security": "ጥበቃ",
  "category.utilities": "የውሃና የመብራት ክፍያ",

  "state.verified": "ተከፍሏል",
  "state.unverified": "ማረጋገጫ የሚጠብቅ",
  "state.unpaid": "አልተከፈለም",
  "state.partial": "በከፊል ተከፍሏል",
  "state.inactive": "አይከፈልም",
};

const TABLE: Record<Lang, Record<Key, string>> = { en, am };

let lang: Lang = read();

/**
 * Amharic until somebody chooses English. Most residents read Amharic first,
 * and the pages put the other language under the first wherever it helps.
 */
function read(): Lang {
  try {
    return localStorage.getItem(LANG_KEY) === "en" ? "en" : "am";
  } catch {
    // A browser with site data switched off still has to draw the page.
    return "en";
  }
}

export function language(): Lang {
  return lang;
}

export function setLanguage(next: Lang): void {
  lang = next;
  try {
    localStorage.setItem(LANG_KEY, next);
  } catch {
    // Not remembering the choice is a smaller problem than not making it.
  }
}

/**
 * Draws this page in one language without changing the reader's choice, for a
 * page that only comes in one. The sign-in page is English; the app still opens
 * in whichever language the person picked inside it.
 */
export function holdLanguage(next: Lang): void {
  lang = next;
}

/**
 * One string, with its blanks filled.
 *
 * A key ending `_one`/`_other` is chosen by `count`, the same way the phone's
 * i18n does it, so a plural is never assembled by hand at a call site.
 */
export function t(key: Key | PluralKey, values: Record<string, string | number> = {}): string {
  const table = TABLE[lang];
  let resolved: string = table[key as Key] ?? en[key as Key] ?? key;
  if (typeof values.count === "number") {
    const plural = `${key}_${values.count === 1 ? "one" : "other"}` as Key;
    if (plural in table) {
      resolved = table[plural];
    }
  }
  return resolved.replace(/{{(\w+)}}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
}

/**
 * The same string in the language the page is not in, for the line under a
 * heading or a tab that says it the other way.
 */
export function other(key: Key, values: Record<string, string | number> = {}): string {
  const table = TABLE[lang === "am" ? "en" : "am"];
  return (table[key] ?? key).replace(/{{(\w+)}}/g, (whole, name: string) =>
    name in values ? String(values[name]) : whole,
  );
}

/** The language the page is not in, for a `lang` attribute on that line. */
export function otherLanguage(): Lang {
  return lang === "am" ? "en" : "am";
}

export const KEYS = Object.keys(en) as Key[];
export { en as ENGLISH, am as AMHARIC };
